'use client';

import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, CircleNotch, EnvelopeSimple } from '@phosphor-icons/react/dist/ssr';
import { signUpTeacher } from '@/actions/auth-signup';
import { AGB_STAND, AGB_VERSION, KANTONE } from '@/lib/legal';

/**
 * Self-Service-Registrierung (Rollout Phase D). Bot-Schutz wie WaitlistForm:
 * Honeypot + Mount-Timestamp; die Server-Action prüft zusätzlich das
 * IP-Rate-Limit. Nach dem Submit: Erfolgs-Screen «Bestätigungs-Mail unterwegs».
 */
export function RegistrierenForm({ referral }: { referral?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [schoolPlace, setSchoolPlace] = useState('');
  const [canton, setCanton] = useState('');
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [partnerInterest, setPartnerInterest] = useState(false);
  const [fachpersonInterest, setFachpersonInterest] = useState(false);
  // Honeypot: unsichtbares Feld — Menschen lassen es leer, Bots füllen es aus.
  const [honey, setHoney] = useState('');
  // Mindest-Ausfüllzeit: Bots submitten in Millisekunden nach Seitenaufbau.
  const [mountedAt] = useState(() => Date.now());
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Sichtbares Feedback statt stummem Abbruch — die native Browser-Meldung
    // greift nicht in jedem Pfad (Autofill/programmatischer Submit).
    if (!email.trim()) {
      setStatus('error');
      setErrorText('Bitte gib deine E-Mail-Adresse ein.');
      return;
    }
    if (password.trim().length < 8) {
      setStatus('error');
      setErrorText('Das Passwort braucht mindestens 8 Zeichen.');
      return;
    }
    if (schoolPlace.trim().length < 2) {
      setStatus('error');
      setErrorText('Bitte gib deine Schule und den Ort an.');
      return;
    }
    if (!canton) {
      setStatus('error');
      setErrorText('Bitte wähle deinen Kanton.');
      return;
    }
    if (!agbAccepted) {
      setStatus('error');
      setErrorText('Bitte akzeptiere die Nutzungsbedingungen.');
      return;
    }
    setStatus('loading');
    setErrorText(null);

    const result = await signUpTeacher(email.trim(), password, {
      honey,
      elapsedMs: Date.now() - mountedAt,
      partnerInterest,
      fachpersonInterest,
      referral,
      schoolPlace: schoolPlace.trim(),
      canton,
      agbAccepted,
    });
    if (result.success) {
      setStatus('success');
    } else {
      setStatus('error');
      setErrorText(result.error);
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-start gap-3 py-5 px-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800">
        <EnvelopeSimple className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="text-left">
          <p className="font-semibold">Bestätigungs-Mail unterwegs!</p>
          <p className="text-sm mt-1 text-emerald-700">
            Öffne den Link in der E-Mail, um deine Adresse zu bestätigen. Danach
            wird dein Konto persönlich freigeschaltet. Du bekommst eine E-Mail,
            sobald es so weit ist.
          </p>
          <p className="text-sm mt-2 text-emerald-700">
            Keine Mail erhalten? Schau im Spam-Ordner nach oder melde dich unter{' '}
            <a href="mailto:kontakt@checkpoint-edu.ch" className="font-semibold underline">
              kontakt@checkpoint-edu.ch
            </a>. Dann richten wir dein Konto gemeinsam ein.
          </p>
          {/* Anti-Enumeration-Ausweg: Bei bereits registrierter Adresse ist die
              Antwort identisch, aber es kommt keine Mail — dieser Absatz gilt
              für alle und verrät nichts über die konkrete Adresse. */}
          <p className="text-sm mt-2 text-emerald-700">
            Hast du hier vielleicht schon einmal ein Konto erstellt? Dann kommt
            keine neue Mail,{' '}
            <Link href="/login" className="font-semibold underline">
              melde dich direkt an
            </Link>{' '}
            oder setze dort unter «Passwort vergessen?» ein neues Passwort.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Honeypot — für Menschen unsichtbar (kein display:none, das erkennen
          manche Bots), aus dem Viewport geschoben + tabIndex -1. Der Feldname
          darf KEINER Autofill-Kategorie entsprechen: «website» füllte
          Chrome-Autofill trotz autoComplete="off" — Signups verschwanden
          still (Vorfall 2026-08-03). */}
      <input
        type="text"
        name="kontrollfeld"
        value={honey}
        onChange={(e) => setHoney(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />
      <label className="block">
        <span className="text-sm font-semibold text-slate-700">E-Mail-Adresse</span>
        <input
          type="email"
          required
          autoComplete="email"
          placeholder="deine@schule.ch"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 text-sm"
        />
      </label>
      <label className="block">
        <span className="text-sm font-semibold text-slate-700">Passwort</span>
        <input
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          placeholder="Mindestens 8 Zeichen"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 text-sm"
        />
      </label>
      <label className="block">
        <span className="text-sm font-semibold text-slate-700">Schule / Ort</span>
        <input
          type="text"
          required
          maxLength={80}
          placeholder="z.B. Primarschule Muster, Winterthur"
          value={schoolPlace}
          onChange={(e) => setSchoolPlace(e.target.value)}
          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 text-sm"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Für die persönliche Freischaltung. Checkpoint richtet sich an Lehrpersonen an
          Schweizer Schulen.
        </span>
      </label>
      <label className="block">
        <span className="text-sm font-semibold text-slate-700">Kanton</span>
        <select
          required
          value={canton}
          onChange={(e) => setCanton(e.target.value)}
          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 text-sm"
        >
          <option value="">Bitte wählen…</option>
          {KANTONE.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </label>
      {/* Bedarfs-Messung fürs Klassen-Team (docs/24 §1a, Signal 3): zwei
          getrennte Häkchen — Stellenpartner (geteilter Datenraum) und
          Fachlehrpersonen (eigenes Fenster) sind unterschiedliche Ausbauten. */}
      <fieldset className="space-y-2.5">
        <legend className="text-sm font-semibold text-slate-700">
          Arbeiten weitere Lehrpersonen mit deiner Klasse?
        </legend>
        <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={partnerInterest}
            onChange={(e) => setPartnerInterest(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
          />
          <span>
            Ich teile mir die Klasse mit einer Stellenpartnerin / einem
            Stellenpartner. Ein geteilter Zugang wäre für uns interessant.
          </span>
        </label>
        <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={fachpersonInterest}
            onChange={(e) => setFachpersonInterest(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
          />
          <span>
            Fachlehrpersonen übernehmen einzelne Stunden in meiner Klasse. Ein
            eigener Zugang für sie wäre interessant.
          </span>
        </label>
      </fieldset>
      {/* Klartext-Box + Click-wrap (docs/26 §9): belastende Klauseln vor der
          Annahme sichtbar machen (Ungewöhnlichkeitsregel, BGE 148 III 57). */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <p className="font-bold text-slate-700">Das Wichtigste zu deiner Zustimmung:</p>
        <ul className="mt-1.5 space-y-1">
          <li>
            <strong className="font-semibold text-slate-700">Pilotbetrieb</strong> ohne
            zugesicherte Verfügbarkeit. Die Beurteilung der Kinder bleibt bei dir und deiner
            Schule (Ziff. 3).
          </li>
          <li>
            <strong className="font-semibold text-slate-700">Echte Klassendaten</strong> erst
            mit Freigabe deiner Schule und Auftragsbearbeitungs-Vereinbarung (Ziff. 4).
          </li>
          <li>
            <strong className="font-semibold text-slate-700">Haftungsbeschränkung</strong> gemäss
            Ziff. 8; <strong className="font-semibold text-slate-700">Änderungen</strong> mit
            Ankündigung, Wesentliches nur mit neuer Zustimmung (Ziff. 9).
          </li>
          <li>
            <strong className="font-semibold text-slate-700">Schweizer Recht</strong>,
            Gerichtsstand am Sitz des Anbieters, zwingende Gerichtsstände vorbehalten
            (Ziff. 12).
          </li>
        </ul>
      </div>
      <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          required
          checked={agbAccepted}
          onChange={(e) => setAgbAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30"
        />
        <span>
          Ich akzeptiere die{' '}
          <a
            href="/agb"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-brand-700 underline"
          >
            Nutzungsbedingungen, Version {AGB_VERSION} vom {AGB_STAND}
          </a>
          .
        </span>
      </label>
      <p className="text-xs text-slate-500">
        Die{' '}
        <a href="/datenschutz" target="_blank" rel="noreferrer" className="font-semibold text-brand-700 underline">
          Datenschutzerklärung
        </a>{' '}
        informiert dich darüber, wie Checkpoint mit Daten umgeht.
      </p>
      <button
        type="submit"
        disabled={status === 'loading' || !agbAccepted}
        className="group inline-flex items-center justify-center rounded-xl bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 active:scale-[0.98] disabled:opacity-60"
      >
        {status === 'loading' ? (
          <CircleNotch className="w-4 h-4 animate-spin" />
        ) : (
          <>
            Konto erstellen
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </>
        )}
      </button>
      {status === 'error' && (
        <p className="text-rose-600 text-sm">
          {errorText ?? 'Etwas ist schiefgelaufen. Versuch es nochmal.'}
        </p>
      )}
      <p className="text-xs text-slate-500">
        Du hast schon ein Konto?{' '}
        <Link href="/login" className="text-brand-700 font-semibold hover:underline">
          Zur Anmeldung
        </Link>
      </p>
    </form>
  );
}
